import React, { useEffect, useRef, useState } from "react";
import { FiCalendar, FiChevronDown, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { fmtPeriodo } from "./boletasFormat";

const MESES_CORTOS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Set", "Oct", "Nov", "Dic"];

const pad = (n) => String(n).padStart(2, "0");
const anioDe = (p) => Number(p.slice(0, 4));

// sumarMeses("2026-01", -1) -> "2025-12"
function sumarMeses(periodo, delta) {
  const [y, m] = periodo.split("-").map(Number);
  const t = y * 12 + (m - 1) + delta;
  return `${Math.floor(t / 12)}-${pad((t % 12) + 1)}`;
}

/**
 * Selector de periodo mensual ("AAAA-MM"):
 *  ◀ [📅 Agosto 2026 ▾] ▶  +  panel con año y cuadrícula de 12 meses.
 * Los meses fuera de [min, max] quedan deshabilitados.
 */
export default function MonthPicker({ id, labelId, value, min = "2020-01", max, onChange }) {
  const [open, setOpen] = useState(false);
  const [anioVista, setAnioVista] = useState(() => anioDe(value));
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const gridRef = useRef(null);

  const anterior = sumarMeses(value, -1);
  const siguiente = sumarMeses(value, 1);
  const enRango = (p) => p >= min && p <= max;

  const cerrar = (devolverFoco = true) => {
    setOpen(false);
    if (devolverFoco) triggerRef.current?.focus();
  };

  const elegir = (p) => {
    if (!enRango(p)) return;
    if (p !== value) onChange(p);
    cerrar();
  };

  // Al abrir: mostrar el año del periodo actual y enfocar su mes.
  useEffect(() => {
    if (!open) return;
    setAnioVista(anioDe(value));
    requestAnimationFrame(() => {
      gridRef.current?.querySelector('[aria-pressed="true"], button:not(:disabled)')?.focus();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Clic fuera cierra el panel.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (!rootRef.current?.contains(e.target)) cerrar(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open]);

  // Flechas dentro de la cuadrícula: ←/→ un mes, ↑/↓ una fila (3 meses).
  const onGridKeyDown = (e) => {
    const pasos = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -3, ArrowDown: 3 }[e.key];
    if (!pasos) return;
    e.preventDefault();
    const botones = [...gridRef.current.querySelectorAll("button")];
    let i = botones.indexOf(document.activeElement);
    do {
      i += pasos;
    } while (i >= 0 && i < botones.length && botones[i].disabled);
    botones[i]?.focus();
  };

  const minY = anioDe(min);
  const maxY = anioDe(max);

  return (
    <div className="mp" ref={rootRef}>
      <div className="mp__control">
        <button
          type="button"
          className="mp__step"
          onClick={() => enRango(anterior) && onChange(anterior)}
          disabled={!enRango(anterior)}
          aria-label={`Mes anterior: ${fmtPeriodo(anterior)}`}
          title="Mes anterior"
        >
          <FiChevronLeft aria-hidden="true" />
        </button>

        <button
          id={id}
          ref={triggerRef}
          type="button"
          className="mp__trigger"
          onClick={() => (open ? cerrar() : setOpen(true))}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-labelledby={labelId ? `${labelId} ${id}-texto` : undefined}
        >
          <FiCalendar className="mp__cal" aria-hidden="true" />
          <span id={`${id}-texto`} className="mp__value">{fmtPeriodo(value)}</span>
          <FiChevronDown className={`mp__caret ${open ? "is-open" : ""}`} aria-hidden="true" />
        </button>

        <button
          type="button"
          className="mp__step"
          onClick={() => enRango(siguiente) && onChange(siguiente)}
          disabled={!enRango(siguiente)}
          aria-label={
            enRango(siguiente)
              ? `Mes siguiente: ${fmtPeriodo(siguiente)}`
              : "Mes siguiente: aún no disponible"
          }
          title={enRango(siguiente) ? "Mes siguiente" : "Aún no disponible"}
        >
          <FiChevronRight aria-hidden="true" />
        </button>
      </div>

      {open && (
        <div
          className="mp__pop"
          role="dialog"
          aria-label="Elegir periodo"
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.stopPropagation();
              cerrar();
            }
          }}
        >
          <div className="mp__year">
            <button
              type="button"
              className="mp__step mp__step--sm"
              onClick={() => setAnioVista((y) => y - 1)}
              disabled={anioVista <= minY}
              aria-label={`Año anterior: ${anioVista - 1}`}
            >
              <FiChevronLeft aria-hidden="true" />
            </button>
            <strong aria-live="polite">{anioVista}</strong>
            <button
              type="button"
              className="mp__step mp__step--sm"
              onClick={() => setAnioVista((y) => y + 1)}
              disabled={anioVista >= maxY}
              aria-label={`Año siguiente: ${anioVista + 1}`}
            >
              <FiChevronRight aria-hidden="true" />
            </button>
          </div>

          <div className="mp__grid" ref={gridRef} onKeyDown={onGridKeyDown}>
            {MESES_CORTOS.map((mes, i) => {
              const p = `${anioVista}-${pad(i + 1)}`;
              const seleccionado = p === value;
              const futuro = p > max;
              return (
                <button
                  key={p}
                  type="button"
                  className={`mp__month ${seleccionado ? "is-selected" : ""}`}
                  onClick={() => elegir(p)}
                  disabled={!enRango(p)}
                  aria-pressed={seleccionado}
                  aria-label={`${fmtPeriodo(p)}${futuro ? ", aún no disponible" : ""}`}
                >
                  {mes}
                </button>
              );
            })}
          </div>

          {value !== max && (
            <button type="button" className="mp__latest" onClick={() => elegir(max)}>
              Ir al último disponible: <strong>{fmtPeriodo(max)}</strong>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
